import type { MetaFunction } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { MapPin, ArrowRight } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Toutes les villes — Artisans vérifiés en France | AfficheServices' },
  { name: 'description', content: 'Trouvez un artisan vérifié dans votre ville. AfficheServices couvre toute la France : Paris, Lyon, Marseille, Bordeaux et des centaines d\'autres villes.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/toutes-villes' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Toutes+les+villes&subtitle=Artisans+en+France' },
]

const MAJOR_CITIES = [
  { name: 'Paris', slug: 'paris', pop: '2.1M' },
  { name: 'Marseille', slug: 'marseille', pop: '870K' },
  { name: 'Lyon', slug: 'lyon', pop: '520K' },
  { name: 'Toulouse', slug: 'toulouse', pop: '490K' },
  { name: 'Nice', slug: 'nice', pop: '340K' },
  { name: 'Nantes', slug: 'nantes', pop: '320K' },
  { name: 'Montpellier', slug: 'montpellier', pop: '290K' },
  { name: 'Strasbourg', slug: 'strasbourg', pop: '280K' },
  { name: 'Bordeaux', slug: 'bordeaux', pop: '260K' },
  { name: 'Lille', slug: 'lille', pop: '235K' },
  { name: 'Rennes', slug: 'rennes', pop: '220K' },
  { name: 'Reims', slug: 'reims', pop: '185K' },
  { name: 'Saint-Étienne', slug: 'saint-etienne', pop: '175K' },
  { name: 'Toulon', slug: 'toulon', pop: '170K' },
  { name: 'Le Havre', slug: 'le-havre', pop: '165K' },
  { name: 'Grenoble', slug: 'grenoble', pop: '160K' },
  { name: 'Dijon', slug: 'dijon', pop: '155K' },
  { name: 'Angers', slug: 'angers', pop: '155K' },
  { name: 'Nîmes', slug: 'nimes', pop: '150K' },
  { name: 'Clermont-Ferrand', slug: 'clermont-ferrand', pop: '145K' },
]

const REGIONS: Record<string, string[]> = {
  'Île-de-France': ['Paris', 'Boulogne-Billancourt', 'Saint-Denis', 'Montreuil', 'Argenteuil', 'Nanterre', 'Créteil', 'Versailles'],
  'Auvergne-Rhône-Alpes': ['Lyon', 'Grenoble', 'Saint-Étienne', 'Clermont-Ferrand', 'Annecy', 'Valence', 'Chambéry'],
  'Nouvelle-Aquitaine': ['Bordeaux', 'Limoges', 'Poitiers', 'La Rochelle', 'Pau', 'Bayonne', 'Angoulême'],
  'Occitanie': ['Toulouse', 'Montpellier', 'Nîmes', 'Perpignan', 'Béziers', 'Narbonne', 'Albi'],
  'Provence-Alpes-Côte d\'Azur': ['Marseille', 'Nice', 'Toulon', 'Aix-en-Provence', 'Avignon', 'Cannes', 'Antibes'],
  'Grand Est': ['Strasbourg', 'Reims', 'Metz', 'Mulhouse', 'Nancy', 'Colmar', 'Troyes'],
  'Hauts-de-France': ['Lille', 'Amiens', 'Roubaix', 'Tourcoing', 'Dunkerque', 'Calais', 'Valenciennes'],
  'Pays de la Loire': ['Nantes', 'Angers', 'Le Mans', 'Saint-Nazaire', 'Laval', 'Cholet'],
  'Bretagne': ['Rennes', 'Brest', 'Quimper', 'Lorient', 'Vannes', 'Saint-Malo', 'Saint-Brieuc'],
  'Normandie': ['Le Havre', 'Rouen', 'Caen', 'Cherbourg', 'Évreux', 'Dieppe'],
}

export default function ToutesVilles() {
  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Artisans vérifiés dans toute la France</h1>
          <p className="mt-3 text-slate-500">Trouvez un professionnel qualifié dans votre ville. Service 100% gratuit.</p>
        </div>

        {/* Major cities grid */}
        <div className="mt-10">
          <h2 className="text-xl font-bold text-slate-900">Grandes villes</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            {MAJOR_CITIES.map((city) => (
              <Link
                key={city.slug}
                to={`/services/plomberie/${city.slug}`}
                className="card-hover flex items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-xs"
              >
                <MapPin className="h-4 w-4 shrink-0 text-primary" />
                <div>
                  <span className="text-sm font-semibold text-slate-900">{city.name}</span>
                  <span className="block text-[10px] text-slate-400">{city.pop} hab.</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* By region */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Par région</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(REGIONS).map(([region, cities]) => (
              <div key={region} className="rounded-xl border border-border bg-white p-5 shadow-xs">
                <h3 className="text-sm font-bold text-slate-900">{region}</h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {cities.map((city) => {
                    const slug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                    return (
                      <Link
                        key={city}
                        to={`/services/plomberie/${slug}`}
                        className="rounded-full border border-slate-100 px-2.5 py-1 text-xs text-slate-600 transition-colors hover:border-primary hover:text-primary"
                      >
                        {city}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link to="/demande-devis" className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  )
}
